import {
  BottomSheet,
  BottomSheetIllustrationContent,
  Text,
  Title,
  type UseModalState,
  useModalState,
} from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { useCallback } from "react";

interface ReturnForSignatureBottomSheetProps {
  modalState: UseModalState;
  isCheckingLocation: boolean;
  isShiftVerified: boolean;
  onRecheckLocation: () => Promise<void>;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ReturnForSignatureBottomSheet(props: ReturnForSignatureBottomSheetProps) {
  const {
    modalState,
    isCheckingLocation,
    isShiftVerified,
    onRecheckLocation,
    onCancel,
    onConfirm,
  } = props;

  const confirmWithoutSignatureModalState = useModalState();

  const handleContinueWithoutSignature = useCallback(() => {
    modalState.closeModal();
    confirmWithoutSignatureModalState.openModal();
  }, [modalState, confirmWithoutSignatureModalState]);

  const handleCancelConfirmation = useCallback(() => {
    confirmWithoutSignatureModalState.closeModal();
    onCancel();
  }, [confirmWithoutSignatureModalState, onCancel]);

  const handleConfirm = useCallback(() => {
    confirmWithoutSignatureModalState.closeModal();
    onConfirm();
  }, [confirmWithoutSignatureModalState, onConfirm]);

  return (
    <>
      <BottomSheet
        modalState={modalState}
        canDismiss={!isCheckingLocation}
        header={<BottomSheetHeader onClose={onCancel} />}
        onClose={onCancel}
      >
        <BottomSheetIllustrationContent
          illustrationType="facility"
          title="Return for a signature"
          description="It looks like you've left the workplace. To avoid approval delays, timesheets should be reviewed and signed by a workplace supervisor whenever possible."
        />
        <DialogFooter orientation="vertical">
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            isLoading={isCheckingLocation}
            onClick={onCancel}
          >
            I will return to the workplace
          </LoadingButton>
          <Button
            fullWidth
            variant="outlined"
            size="large"
            disabled={isCheckingLocation}
            onClick={handleContinueWithoutSignature}
          >
            Continue without signature
          </Button>
          <TextButton size="large" disabled={isCheckingLocation} onClick={onRecheckLocation}>
            I&apos;m still at the workplace
          </TextButton>
        </DialogFooter>
      </BottomSheet>

      {/* Confirmation Bottom Sheet */}
      <BottomSheet
        modalState={confirmWithoutSignatureModalState}
        header={<BottomSheetHeader onClose={handleCancelConfirmation} />}
        onClose={handleCancelConfirmation}
      >
        <Stack spacing={4} sx={{ paddingX: 5 }}>
          <Title component="h2" sx={{ textWrap: "balance", textAlign: "center" }}>
            Are you sure?
          </Title>
          <Text
            semibold
            variant="body1"
            sx={{ textAlign: "center", color: (theme) => theme.palette.text.secondary }}
          >
            {isShiftVerified
              ? "Requests for shift adjustment are more likely to be rejected without a signature from a workplace employee."
              : "Requests for shift payment are more likely to be rejected without a signature from a workplace employee."}
          </Text>
        </Stack>
        <DialogFooter orientation="vertical" sx={{ marginTop: 10 }}>
          <Button fullWidth variant="contained" size="large" onClick={handleCancelConfirmation}>
            I will return to the workplace
          </Button>
          <Button fullWidth variant="outlined" size="large" onClick={handleConfirm}>
            Continue without signature
          </Button>
        </DialogFooter>
      </BottomSheet>
    </>
  );
}
